/**
 * 境界数据定义
 * 9大境界，每境9层
 */

export interface RealmLevel {
  /** 唯一标识，如 "qi_refining_3" */
  key: string;
  /** 境界名称 */
  realmName: string;
  /** 当前层数 1-9 */
  stage: number;
  /** 突破所需修炼经验 */
  expRequired: number;
  /** 属性倍率（基础属性 × 倍率） */
  statMultiplier: number;
  /** 突破成功率 (0~1) */
  breakthroughRate: number;
  /** 每秒被动修炼经验 */
  passiveExpPerSec: number;
}

export interface RealmConfig {
  name: string;
  key: string;
  description: string;
  levels: RealmLevel[];
}

/**
 * 生成某一境界的9层数据
 */
function createRealmLevels(
  realmKey: string,
  realmName: string,
  baseExp: number,
  baseMultiplier: number,
  baseBreakthroughRate: number,
  basePassiveExp: number
): RealmLevel[] {
  const levels: RealmLevel[] = [];
  for (let stage = 1; stage <= 9; stage++) {
    levels.push({
      key: `${realmKey}_${stage}`,
      realmName,
      stage,
      expRequired: Math.floor(baseExp * Math.pow(1.5, stage - 1)),
      statMultiplier: baseMultiplier + (stage - 1) * 0.3,
      breakthroughRate: Math.max(0.05, baseBreakthroughRate - (stage - 1) * 0.03),
      passiveExpPerSec: basePassiveExp * Math.pow(1.2, stage - 1),
    });
  }
  return levels;
}

export const REALMS: RealmConfig[] = [
  {
    name: '炼气期',
    key: 'qi_refining',
    description: '引气入体，淬炼肉身',
    levels: createRealmLevels('qi_refining', '炼气期', 100, 1.0, 0.9, 0.5),
  },
  {
    name: '筑基期',
    key: 'foundation',
    description: '筑就道基，脱胎换骨',
    levels: createRealmLevels('foundation', '筑基期', 300, 3.5, 0.8, 1.2),
  },
  {
    name: '金丹期',
    key: 'golden_core',
    description: '凝结金丹，超凡入圣',
    levels: createRealmLevels('golden_core', '金丹期', 800, 7.0, 0.7, 2.5),
  },
  {
    name: '元婴期',
    key: 'nascent_soul',
    description: '金丹化婴，元神初成',
    levels: createRealmLevels('nascent_soul', '元婴期', 2000, 12.0, 0.6, 5.0),
  },
  {
    name: '化神期',
    key: 'divinity',
    description: '元神大成，化凡为神',
    levels: createRealmLevels('divinity', '化神期', 5000, 20.0, 0.5, 10.0),
  },
  {
    name: '炼虚期',
    key: 'void_refining',
    description: '炼虚合道，返璞归真',
    levels: createRealmLevels('void_refining', '炼虚期', 12000, 32.0, 0.4, 20.0),
  },
  {
    name: '合体期',
    key: 'unity',
    description: '天人合一，道法自然',
    levels: createRealmLevels('unity', '合体期', 28000, 50.0, 0.3, 40.0),
  },
  {
    name: '大乘期',
    key: 'mahayana',
    description: '大道圆满，只差一步',
    levels: createRealmLevels('mahayana', '大乘期', 65000, 75.0, 0.2, 80.0),
  },
  {
    name: '渡劫期',
    key: 'tribulation',
    description: '天劫降临，成则飞升',
    levels: createRealmLevels('tribulation', '渡劫期', 150000, 110.0, 0.1, 160.0),
  },
];

/** 查找境界 */
export function findRealmLevel(key: string): RealmLevel | undefined {
  for (const realm of REALMS) {
    const found = realm.levels.find((l) => l.key === key);
    if (found) return found;
  }
  return undefined;
}

/** 获取下一层境界 */
export function getNextRealmLevel(currentKey: string): RealmLevel | undefined {
  for (let i = 0; i < REALMS.length; i++) {
    const realm = REALMS[i];
    const idx = realm.levels.findIndex((l) => l.key === currentKey);
    if (idx !== -1) {
      if (idx < 8) {
        // 同一境界的下一层
        return realm.levels[idx + 1];
      } else if (i < REALMS.length - 1) {
        // 下一境界的第1层
        return REALMS[i + 1].levels[0];
      }
      // 已是最后一层（渡劫期9层）
      return undefined;
    }
  }
  return undefined;
}

/** 获取某境界的显示名称 */
export function getRealmDisplayName(key: string): string {
  const level = findRealmLevel(key);
  if (!level) return '未知境界';
  return `${level.realmName} 第${level.stage}层`;
}
